<template>
  <div>
    <draggable
      v-model="questions"
      :disabled="!allows_crud"
      v-bind="dragOptions"
      class="list-group"
      group="question"
      handle=".handle"
    >
      <transition-group type="transition" name="flip-list">
        <template>
          <div
            class="container-question"
            v-for="question in questions"
            :key="`question-dragable-${question.id}-${question.test}-${question.question_type}`"
          >
            <NewQuestionCard
              :ref="`question-dragable-${question.id}-${question.test}-${question.question_type}`"
              :container_instrument_id="container_instrument_id"
              :allows_crud="allows_crud"
              :Question="question"
              :segment_order="segment_order"
              :isLoading="isLoading"
              :evaluation_criteria_micro_ids="evaluation_criteria_micro_ids"
              :preview_evaluation="preview_evaluation"
              :checked_score="checked_score"
              :checked_weightning="checked_weightning"
              :evaluatee_view="evaluatee_view"
              :evaluator_view="evaluator_view"
              :is_running_test="is_running_test"
              :finished_test="finished_test"
              :user_id="user_id"
              :evaluation_situations="evaluation_situations"
              :closedEvaluationScore="closedEvaluationScore"
              :is_published="is_published"
            ></NewQuestionCard>
          </div>
        </template>
      </transition-group>
    </draggable>
    <div class="container-buttons mb-2">
      <ButtonMedium
        v-if="
          allows_crud &&
          test &&
          !test.question_creation_is_locked &&
          (!evaluationSituation ||
            (evaluationSituation &&
              ![
                'duoc_ejecucion_practica_test',
                'duoc_entrega_de_encargo_test',
                'duoc_presentacion_test',
              ].includes(evaluationSituation.internal_use_id) &&
              evaluationSituation.enable_questions &&
              (evaluationSituation.enable_redaction_questions ||
                evaluationSituation.enable_simple_selection_questions ||
                evaluationSituation.enable_multiple_selection_questions ||
                evaluationSituation.enable_matching_questions)))
        "
        :tooltip_text="``"
        :text_button="
          segment_id
            ? $t('evaluations.newEvaluations.addQuestionInsideSegment', {
                entity: $getVisibleNames(
                  'evaluations2.testsegment',
                  false,
                  'Segmento'
                ),
              })
            : $t('evaluations.newEvaluations.addQuestion')
        "
        :click_button="openModalCreateQuestion"
        :icon="'plus'"
        :variant="'primary'"
        :custom_class="`mt-2`"
      >
      </ButtonMedium>
      <!-- E54 · Generar las preguntas con IA desde lo que la pieza del Diseño
           Instruccional ya declaró. Va al lado de "Agregar Pregunta" y con sus
           mismas condiciones: donde no se puede agregar una a mano, tampoco se
           genera. Lo que suma son las dos del componente de IA (institución y
           usuario) y que la evaluación venga del diseño, que es de donde sale
           todo el contexto. -->
      <AiQuestionsBuilderModal
        v-if="showsAiQuestionsBuilder"
        :test_id="instrument_id"
        :segment_id="segment_id"
        :evaluation_criteria_micro_ids="evaluation_criteria_micro_ids"
        :evaluation_situations="evaluation_situations || []"
        :next_order="nextQuestionOrder"
        :piece_name="designPieceName"
        :button_class="`mt-2 ml-2`"
      ></AiQuestionsBuilderModal>
    </div>
    <b-modal
      :id="`modal-created-question-${instrument_id}-${segment_id}-${container_instrument_id}`"
      :title="$t('evaluations.newEvaluations.createQuestion')"
      hide-footer
      size="lg"
      ignore-enforce-focus-selector="#sidebar-right"
      @hide="hideModalQuestion"
    >
      <NewQuestionForm
        :test_id="instrument_id"
        :segment_id="segment_id"
        :evaluation_criteria_micro_ids="evaluation_criteria_micro_ids"
        :order="
          questions.length > 0 ? questions[questions.length - 1].order + 1 : 1
        "
        :evaluation_situations="evaluation_situations"
        @closeModal="
          $bvModal.hide(
            `modal-created-question-${instrument_id}-${segment_id}-${container_instrument_id}`
          )
        "
      ></NewQuestionForm>
    </b-modal>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import draggable from "vuedraggable";

export default {
  name: "QuestionsContainer",
  components: {
    draggable,
    NewQuestionCard: () => import("./NewQuestionCard"),
    NewQuestionForm: () =>
      import(
        "@/components/new-evaluations/Evaluation/Questions/NewQuestionForm"
      ),
    AiQuestionsBuilderModal: () =>
      import("@/components/reusable/AiQuestionsBuilderModal"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
  },
  props: {
    instrument_id: { type: Number, required: true },
    segment_id: { type: [Number, null], default: null },
    // Orden mostrado del segmento; con orden aleatorio difiere del store.
    segment_order: { type: Number, default: null },
    allows_crud: { type: Boolean, required: true },
    isLoading: { type: Boolean, required: true },
    preview_evaluation: {
      type: Boolean,
      required: true,
    },
    container_instrument_id: {
      type: String,
    },
    checked_score: {
      type: Boolean,
    },
    evaluation_criteria_micro_ids: {
      type: Array,
      required: true,
    },
    checked_weightning: {
      type: Boolean,
    },
    evaluatee_view: {
      type: Boolean,
      default: false,
    },
    evaluator_view: {
      type: Boolean,
      default: false,
    },
    is_running_test: {
      type: Boolean,
      default: false,
    },
    finished_test: {
      type: Boolean,
      default: false,
    },
    // para mostrar el resultado del evaluado al profesor.
    user_id: {
      type: Number,
      required: false,
    },
    // para restringir que el evaluador pueda colocar notas fuera del periodo actual con respecto al de la sección.
    closedEvaluationScore: {
      type: Boolean,
      default: false,
    },
    evaluation_situations: {
      type: Array,
    },
    is_published: {
      type: Boolean,
    },
  },
  data() {
    return {
      shuffledQuestions: null,
    };
  },
  computed: {
    ...mapGetters({
      redaction_questions: names.NEW_REDACTION_QUESTIONS,
      multiple_choice_questions: names.NEW_MULTIPLE_CHOICE_QUESTIONS,
      filling_questions: names.NEW_FILLING_QUESTIONS,
      matching_questions: names.NEW_MATCHING_QUESTIONS,
      tests: names.NEW_TESTS,
      evaluations: names.NEW_EVALUATIONS,
      user: "getUser",
      institution: "getInstitution",
      egress_profile_matters: names.EGRESS_PROFILES_MATTERS,
      sections: names.SECTIONS,
    }),
    test() {
      return this.tests.find((x) => x.id == this.instrument_id);
    },
    evaluationSituation() {
      if (!this.test) return null;
      return this.evaluation_situations.find(
        (x) => x.id == this.test.evaluation_situation
      );
    },
    evaluation() {
      if (!this.test) return null;
      return this.evaluations.find((x) => x.id == this.test.evaluation);
    },
    section() {
      if (this.evaluation && this.evaluation.scope == 1)
        return this.sections.find((x) => x.id == this.evaluation.scope_id);
      else return null;
    },
    egressProfileMatter() {
      if (
        this.evaluation &&
        this.evaluation.scope == 2 &&
        this.evaluation.scope_id != null
      ) {
        return this.egress_profile_matters.find(
          (x) => x.id == this.evaluation.scope_id
        );
      }
      if (
        this.evaluation &&
        this.evaluation.scope == 1 &&
        this.section &&
        this.section.egress_profile_matter != null
      ) {
        return this.egress_profile_matters.find(
          (x) => x.id == this.section.egress_profile_matter
        );
      }
      return null;
    },
    baseQuestions() {
      let list = [];
      list = list.concat(
        this.redactionQuestions.map((question) => ({
          ...question,
          question_type: 1,
        }))
      );
      list = list.concat(
        this.multipleChoiceQuestions.map((question) => ({
          ...question,
          question_type: 2,
        }))
      );
      // list = list.concat(
      //   this.fillingQuestions.map((question) => ({
      //     ...question,
      //     question_type: 3,
      //   }))
      // );
      list = list.concat(
        this.matchingQuestions.map((question) => ({
          ...question,
          question_type: 4,
        }))
      );
      return list.sort((a, b) => a.order - b.order);
    },
    shouldApplyRandomOrder() {
      return (
        ((this.preview_evaluation && !this.evaluator_view) ||
          (this.evaluatee_view &&
            this.is_running_test &&
            !this.is_published)) &&
        this.test &&
        this.test.evaluatee_random_questions
      );
    },
    questions: {
      get() {
        if (this.shouldApplyRandomOrder) {
          return this.shuffledQuestions || this.baseQuestions;
        }
        return this.baseQuestions;
      },
      set(list) {
        list.forEach((element, index) => {
          if (!isNaN(element.id)) {
            if (element.question_type == 1) {
              let payload = {
                new_redaction_question_id: element.id,
                item: {
                  order: index + 1,
                  segment: this.segment_id,
                },
              };
              this.$store.dispatch(names.PATCH_NEW_REDACTION_QUESTION, payload);
            } else if (element.question_type == 2) {
              let payload = {
                new_multiple_choice_question_id: element.id,
                item: {
                  order: index + 1,
                  segment: this.segment_id,
                },
              };
              this.$store.dispatch(
                names.PATCH_NEW_MULTIPLE_CHOICE_QUESTION,
                payload
              );
            } else if (element.question_type == 3) {
              let payload = {
                new_filling_question_id: element.id,
                item: {
                  order: index + 1,
                  segment: this.segment_id,
                },
              };
              this.$store.dispatch(names.PATCH_NEW_FILLING_QUESTION, payload);
            } else if (element.question_type == 4) {
              let payload = {
                new_matching_question_id: element.id,
                item: {
                  order: index + 1,
                  segment: this.segment_id,
                },
              };
              this.$store.dispatch(names.PATCH_NEW_MATCHING_QUESTION, payload);
            }
          }
        });
      },
    },
    dragOptions() {
      return {
        animation: 0,
        group: "question",
        disabled: false,
        ghostClass: "ghost",
      };
    },
    // El `order` de la primera pregunta nueva: al final de lo que ya hay en
    // esta zona. Misma expresion que el `:order` del formulario manual.
    nextQuestionOrder() {
      return this.questions.length > 0
        ? this.questions[this.questions.length - 1].order + 1
        : 1;
    },
    // La evaluación viene del Diseño Instruccional, directo o por la plantilla
    // de la que se replicó. Sin eso no hay pieza de la que tomar el contexto y
    // el botón no tiene nada que ofrecer.
    hasDesignLink() {
      if (!this.evaluation) return false;
      return (
        this.evaluation.matter_evaluation != null ||
        this.evaluation.egress_profile_matter_evaluation != null ||
        this.evaluation.parent_evaluation != null
      );
    },
    designPieceName() {
      if (!this.hasDesignLink || !this.evaluation) return "";
      // El título de la Evaluación lo sincroniza la pieza en cada guardado
      // (`_sync_with_evaluation_instance`), así que es el nombre de la pieza.
      return this.evaluation.title || "";
    },
    showsAiQuestionsBuilder() {
      if (!this.allows_crud || this.preview_evaluation) return false;
      if (this.evaluatee_view || this.evaluator_view) return false;
      if (!this.test || this.test.question_creation_is_locked) return false;
      // Ya no se exige la evaluación planificada: el backend resuelve la
      // Asignatura por el alcance de la evaluación cuando no hay planificada
      // (`_instrument_anchor`), así que el botón también sirve en la
      // evaluación que alguien agregó a mano en su Sección. Lo que de verdad
      // hace falta son los Indicadores de Logro: son lo que se mide.
      if (this.evaluation_criteria_micro_ids.length === 0) return false;
      // Los mismos tipos que gobiernan el botón de agregar: si la Situación
      // Evaluativa no habilita ninguno, este Instrumento se evalúa con pauta.
      if (
        this.evaluationSituation &&
        (!this.evaluationSituation.enable_questions ||
          !(
            this.evaluationSituation.enable_redaction_questions ||
            this.evaluationSituation.enable_simple_selection_questions ||
            this.evaluationSituation.enable_multiple_selection_questions
          ))
      )
        return false;
      if (!this.user || !this.institution) return false;
      if (!this.institution.kl_ai_quick_creation) return false;
      if (this.user.user_data && !this.user.user_data.ai_component)
        return false;
      if (this.user.is_superuser) return true;
      // Mientras dure la prueba, sólo los dos grupos directivos de arriba: lo que
      // la generación escribe le llega al estudiante. Es más estrecho que los
      // demás builders del Programa, que aceptan también Facultad y Dirección de
      // Carrera, y es el mismo criterio con el que `InstructionalView` abre el DI.
      // Al ampliarlo hay que tocar los otros dos botones de IA del módulo
      // —generar preguntas, generar pauta, regenerar una pregunta— y
      // `AI_EVALUATION_BUILDER_GROUPS` del backend.
      return [
        "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
        "DIRECTIVOS ACADÉMICOS (CAMPUS)",
      ].some((group) => (this.user.group_names || []).includes(group));
    },
    redactionQuestions() {
      return this.redaction_questions.filter(
        (x) => x.test == this.instrument_id && x.segment == this.segment_id
      );
    },
    multipleChoiceQuestions() {
      return this.multiple_choice_questions.filter(
        (x) => x.test == this.instrument_id && x.segment == this.segment_id
      );
    },
    fillingQuestions() {
      return this.filling_questions.filter(
        (x) => x.test == this.instrument_id && x.segment == this.segment_id
      );
    },
    matchingQuestions() {
      return this.matching_questions.filter(
        (x) => x.test == this.instrument_id && x.segment == this.segment_id
      );
    },
  },
  methods: {
    insertStoreOpenAiQuestion() {
      this.$store.commit(names.MUTATE_DELETE_FILTER_INTELLIGENCE);
      this.$store.commit(names.MUTATE_DELETE_RULES_FILTER_SELECTOR);

      this.$store.commit(names.MUTATE_RULES_FILTER_SELECTOR, {
        visible_intelligence_options: [8],
        disabled_selected_intelligence_option: true,
      });
    },
    openModalCreateQuestion() {
      this.insertStoreOpenAiQuestion();
      if (
        this.evaluation &&
        this.evaluation.scope == 3 &&
        this.evaluation.scope_id != null
      ) {
        this.$nextTick(() => {
          this.$store.commit(names.MUTATE_FILTER_INTELLIGENCE, {
            matter_id: this.evaluation.scope_id,
            available_evaluation_criteria_micro:
              this.evaluation_criteria_micro_ids,
            extra_label: this.$t("evaluations.newEvaluations.question"),
            extra_context: this.$t(
              "evaluations.newEvaluations.forTheEvaluation"
            ),
            intelligence_option: 8,
            sub_option: 3,
            show_message_evaluation_view: true,
          });
        });
      }
      if (
        this.egressProfileMatter &&
        this.evaluation &&
        (this.evaluation.scope == 2 || this.evaluation.scope == 1) &&
        this.evaluation.scope_id != null
      ) {
        this.$nextTick(() => {
          this.$store.commit(names.MUTATE_FILTER_INTELLIGENCE, {
            matter_id: this.egressProfileMatter.matter,
            available_evaluation_criteria_micro:
              this.evaluation_criteria_micro_ids,
            extra_label: this.$t("evaluations.newEvaluations.question"),
            extra_context: this.$t(
              "evaluations.newEvaluations.forTheEvaluation"
            ),
            intelligence_option: 8,
            sub_option: 3,
            show_message_evaluation_view: true,
          });
        });
      }
      this.$bvModal.show(
        `modal-created-question-${this.instrument_id}-${this.segment_id}-${this.container_instrument_id}`
      );
    },
    hideModalQuestion() {
      this.$store.commit(names.MUTATE_DELETE_FILTER_INTELLIGENCE);
      this.$store.commit(names.MUTATE_DELETE_RULES_FILTER_SELECTOR);
      this.$store.commit(names.MUTATE_RULES_FILTER_SELECTOR, {
        visible_intelligence_options: [7],
      });

      if (
        this.evaluation &&
        this.evaluation.scope == 3 &&
        this.evaluation.scope_id != null
      ) {
        this.$nextTick(() => {
          this.$store.commit(names.MUTATE_FILTER_INTELLIGENCE, {
            matter_id: this.evaluation.scope_id,
            extra_context: this.$t(
              "evaluations.newEvaluations.forTheEvaluation"
            ),
            extra_label: "",
            show_message_evaluation_view: true,
            intelligence_option: 7,
          });
        });
      }
      if (
        this.egressProfileMatter &&
        this.evaluation &&
        (this.evaluation.scope == 2 || this.evaluation.scope == 1) &&
        this.evaluation.scope_id != null
      ) {
        this.$nextTick(() => {
          this.$store.commit(names.MUTATE_FILTER_INTELLIGENCE, {
            matter_id: this.egressProfileMatter.matter,
            extra_context: this.$t(
              "evaluations.newEvaluations.forTheEvaluation"
            ),
            show_message_evaluation_view: true,
            intelligence_option: 7,
          });
        });
      }
    },
    scrollIntoView(payload) {
      this.$nextTick(() => {
        setTimeout(() => {
          const reft =
            this.$refs[
              `question-dragable-${payload.question.id}-${payload.question.test}-${payload.question.question_type}`
            ];
          if (reft && reft.length > 0 && reft[0].$el)
            reft[0].$el.scrollIntoView(payload.options); // Hacer scroll al elemento
        }, 500); // Retraso de 100 ms
      });
    },
    generateShuffledQuestions() {
      const list = [...this.baseQuestions];
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
      // Update the order property to match the new shuffled positions
      const shuffledWithCorrectOrder = list.map((question, index) => ({
        ...question,
        order: index + 1,
      }));
      this.shuffledQuestions = shuffledWithCorrectOrder;
    },
  },
  watch: {
    preview_evaluation(newVal, oldVal) {
      // Reset shuffled questions when preview mode changes
      if (newVal !== oldVal) {
        this.shuffledQuestions = null;
      }
    },
    shouldApplyRandomOrder: {
      handler(newVal) {
        if (
          newVal &&
          !this.shuffledQuestions &&
          this.baseQuestions.length > 0
        ) {
          this.generateShuffledQuestions();
          // Emitir evento global para reinicializar componentes de texto enriquecido
          this.$nextTick(() => {
            this.$root.$emit("reinitialize-rich-text-components");
          });
        } else if (!newVal) {
          this.shuffledQuestions = null;
        }
      },
      immediate: true,
    },
    // Watcher para baseQuestions - regenera shuffle cuando llegan nuevos datos
    baseQuestions: {
      handler(newQuestions, oldQuestions) {
        // Solo regenerar si debería aplicar random order y:
        // 1. No hay shuffled questions aún, O
        // 2. El número de preguntas cambió (llegaron nuevas preguntas)
        if (this.shouldApplyRandomOrder && newQuestions.length > 0) {
          if (
            !this.shuffledQuestions ||
            (oldQuestions && newQuestions.length !== oldQuestions.length)
          ) {
            this.$nextTick(() => {
              this.generateShuffledQuestions();
            });
          }
        }
      },
      immediate: false,
    },
  },
  created() {},
};
</script>

<style scoped>
.container-question {
  background: white;
  border-radius: 5px;
}
.container-buttons {
  display: flex;
  justify-content: center;
}
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
</style>

